import { useState } from 'react'
import { api } from '../api'
import { ApiError } from '../types'
import { Icon } from '../components/Icon'
import { Bar } from '../components/ui'
import { Banner, Eyebrow, Note, Screen, Shell } from '../components/shell'

/** Ô email + mật khẩu, dùng chung cho đăng nhập và đăng ký. */
function Credentials({
  onDone,
  mode,
  cta,
  footer,
  onSwitch,
}: {
  onDone: () => void
  /** Chọn API nào gọi. Tách khỏi `cta` vì nhãn nút là chuyện trình bày,
   *  còn hành vi là chuyện logic — gộp chung thì đổi câu chữ là hỏng chức năng. */
  mode: 'login' | 'register'
  cta: string
  footer: ReactNodeish
  onSwitch: ReactNodeish
}) {
  const [email, setEmail] = useState('')
  const [pw, setPw] = useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (busy) return
    setErr('')
    setBusy(true)
    try {
      if (mode === 'login') await api.login(email, pw)
      else await api.register(email, pw)
      onDone()
    } catch (err) {
      const a = err as ApiError
      // Với sai mật khẩu, backend trả lời GIỐNG HỆT trường hợp email không
      // tồn tại (chống dò tài khoản). Hiện nguyên văn `detail` là đúng và
      // không tiết lộ gì — nên phần này cố ý không rút gọn thành "sai thông
      // tin" cũng không thêm gì.
      setErr(a.detail || 'Không đăng nhập được.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <form onSubmit={submit} className="space-y-3">
        {err && <Banner>{err}</Banner>}
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Email</span>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            inputMode="email"
            required
            autoFocus
            className="input"
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Mật khẩu</span>
          <input
            type="password"
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            // Tự động hoàn đúng kiểu cho cả hai màn: đăng ký muốn "mới",
            // đăng nhập muốn "có sẵn". Một component, hai hành vi.
            autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
            minLength={8}
            required
            className="input"
          />
        </label>
        <button type="submit" disabled={busy} className={'btn btn-primary ' + (busy ? 'btn-off' : '')}>
          {busy ? 'Đang xử lý…' : cta}
          {!busy && <Icon name="arrow" size={18} />}
        </button>
      </form>
      {footer}
      <div className="mt-2">{onSwitch}</div>
    </>
  )
}

type ReactNodeish = React.ReactNode

export function Welcome({
  onStart,
  onLogin,
  loggedIn,
}: {
  onStart: () => void
  onLogin: () => void
  loggedIn: boolean
}) {
  return (
    <Shell>
      <Screen className="justify-between">
        <div>
          <div className="font-serif text-3xl font-semibold text-accent">Sẵn Sàng</div>
          <Note className="mt-1.5">
            Luyện thi đánh giá năng lực hành nghề Bác sĩ Y khoa
          </Note>
        </div>

        {/* Ba thanh trang trí. 80/55/68 là số bịa, còn 128 vấn đề chuyên môn
            thì ĐÚNG (đếm được trong bảng core_issues) — nên chỉ giữ 128, bỏ
            ba con số kia. Thanh không mang thông tin thì `decorative` để
            trình đọc màn hình khỏi đọc nhầm là "80% hoàn thành". */}
        <div className="card space-y-5">
          <Bar percent={80} tone="bg-gold" decorative />
          <Bar percent={55} decorative />
          <Bar percent={68} tone="bg-good" decorative />
          <Note className="!mt-1">Bản đồ 128 vấn đề chuyên môn cốt lõi</Note>
        </div>

        <div>
          {loggedIn ? (
            <button onClick={onStart} className="btn btn-primary">
              Tiếp tục
              <Icon name="arrow" size={18} />
            </button>
          ) : (
            <>
              <button onClick={onStart} className="btn btn-primary mb-2">
                Bắt đầu
                <Icon name="arrow" size={18} />
              </button>
              <button onClick={onLogin} className="btn btn-ghost">
                Đã có tài khoản? Đăng nhập
              </button>
            </>
          )}
          <Note className="mt-2 text-center">
            Không phải hệ thống thi chính thức của Hội đồng Y khoa Quốc gia.
          </Note>
        </div>
      </Screen>
    </Shell>
  )
}

export function Register({ onDone, onGoLogin }: { onDone: () => void; onGoLogin: () => void }) {
  return (
    <Shell>
      <Screen>
        <Eyebrow>Bước 1 / 2</Eyebrow>
        <h1 className="mt-1.5">Tạo tài khoản</h1>
        <Note className="mt-1 mb-6">
          Miễn phí 200 câu mẫu. Không cần thẻ, không cần xác minh.
        </Note>
        <Credentials
          onDone={onDone}
          mode="register"
          cta="Tạo tài khoản"
          onSwitch={
            <button onClick={onGoLogin} className="btn btn-ghost">
              Đã có tài khoản? Đăng nhập
            </button>
          }
          footer={<Note className="mt-4">Mật khẩu tối thiểu 8 ký tự.</Note>}
        />
      </Screen>
    </Shell>
  )
}

export function Login({ onDone, onGoRegister }: { onDone: () => void; onGoRegister: () => void }) {
  return (
    <Shell>
      <Screen>
        <Eyebrow>Bước 2 / 2</Eyebrow>
        <h1 className="mt-1.5 mb-6">Đăng nhập</h1>
        <Credentials
          onDone={onDone}
          mode="login"
          cta="Đăng nhập"
          onSwitch={
            <button onClick={onGoRegister} className="btn btn-ghost">
              Chưa có tài khoản? Đăng ký
            </button>
          }
          footer={<Note className="mt-4">Tiến độ lưu theo tài khoản, xoá tài khoản là mất.</Note>}
        />
      </Screen>
    </Shell>
  )
}

